import useGlobalStore from "~/store/global"

/** Key `multi-tenant-select.tsx` persists the BU selection under. */
const LINKED_TENANT_STORAGE_KEY = "linked_tenant_id"

/**
 * The BU selection survives a reload in sessionStorage, but the global store is
 * only rehydrated from it inside `MultiTenantSelect` — which renders on
 * accounts / accountos.overview / command-center / crm-pipeline / people-list
 * and NOT on `/os/agents/*`. The store has no persist middleware either, so on
 * a fresh load of a chat route `store.linkedTenantId` is 0 while storage still
 * holds the real id. Read storage as the fallback so chat routes see it. (ENG-3729)
 */
function selectedLinkedTenantId(): number {
  const fromStore = useGlobalStore.getState().linkedTenantId
  if (fromStore) return fromStore
  if (typeof window === "undefined") return 0
  try {
    return Number(window.sessionStorage.getItem(LINKED_TENANT_STORAGE_KEY)) || 0
  } catch {
    // Storage can throw outright (Safari private mode, blocked site data).
    return 0
  }
}

/**
 * Append the linked tenant the user is working in to a request URL. (ENG-3729)
 *
 * api-getorbitx's `authenticate` reads `linked_tenant_id` off the query string
 * and switches `req.tenant_id` for the request. Mirrors py-processing's
 * `createUrl()` (app/utilities/common.py).
 *
 * Returns the url untouched when nothing is selected or the selection echoes
 * the user's own tenant. `linkedTenantId` is optional — pass it when the caller
 * already has the id, omit it to read the active selection.
 */
export function withLinkedTenant(url: string, linkedTenantId?: number): string {
  const id = linkedTenantId ?? selectedLinkedTenantId()
  if (!id || id === useGlobalStore.getState().tokenInfo?.tenant_id) return url
  return `${url}${url.includes("?") ? "&" : "?"}linked_tenant_id=${id}`
}
